import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { MultipleLocationDetailsBarProps } from './multipleLocationsDetailsBarProps';
import { SideBarSimpleMethodItem } from '../../../components/commons/SideBarComponents/SimpleMethodItem/sideBarSimpleMethodButtonItem';
import { StyledContainerItems } from '../../../components/commons/SideBarComponents/sideBarComponentsStyles';
import { viewRightSideBarEnum } from '../../../constants/sidebarEnums';
import Strings from '../../../i18n';
import { SideBarDeleteActionWithPopup } from '../../../components/commons/SideBarComponents/DeleteActionWithPopup/sideBarDeleteActionWithPopup';
import {
  useDeleteLocationByRoleMutation,
  useResetLocationMutation,
  useTurnOffLocationMutation,
  useTurnOnLocationMutation
} from '../../../services/locations/locationsService';
import { useDeleteOrganizationMutation } from '../../../services/device/deviceService';
import { SideBarResetActionWithPopup } from '../../../components/commons/SideBarComponents/ResetActionWithPopup/sideBarResetActionWithPopup';

export const MultipleLocationsDetailsSideBar = ({
  openRightSideBarForMethod,
  reloadTable,
  selectedLocations,
  selectedOrganizations,
  setOpenRightSideBar,
  selectedLocationsData
}: MultipleLocationDetailsBarProps) => {
  const [
    deleteLocationByRole,
    {
      isSuccess: isSuccessDeleteLocationByRole,
      isError: isErrorDeleteLocationByRole,
      error: errorDeleteLocationByRole
    }
  ] = useDeleteLocationByRoleMutation();

  useEffect(() => {
    if (isErrorDeleteLocationByRole && errorDeleteLocationByRole) {
      if (
        'status' in errorDeleteLocationByRole &&
        'data' in errorDeleteLocationByRole
      ) {
        const errorData = errorDeleteLocationByRole.data as {
          message?: string;
        };
        toast.error(errorData.message || Strings.common.somethingWentWrong);
      } else {
        toast.error(Strings.common.somethingWentWrong);
      }
    }
  }, [isErrorDeleteLocationByRole, errorDeleteLocationByRole]);

  const [deleteOrganization, { isSuccess: isSuccessDeleteOrganization }] =
    useDeleteOrganizationMutation();

  const [resetLocation, { isSuccess: isSuccessResetLocation }] =
    useResetLocationMutation();

  useEffect(() => {
    if (isSuccessResetLocation) {
      toast.success(Strings.locations.resetLocationSuccess);
      setOpenRightSideBar(false);
      reloadTable();
    }
  }, [isSuccessResetLocation]);

  useEffect(() => {
    if (isSuccessDeleteLocationByRole) {
      toast.success(Strings.locations.successDeleteLocation);
      setOpenRightSideBar(false);
      reloadTable();
    }
  }, [isSuccessDeleteLocationByRole]);

  const [turnOffLocation, { isSuccess: isSuccessTurnOffLocation }] =
    useTurnOffLocationMutation();

  const [turnOnLocation, { isSuccess: isSuccessTurnOnLocation }] =
    useTurnOnLocationMutation();

  useEffect(() => {
    if (isSuccessTurnOffLocation || isSuccessTurnOnLocation) {
      toast.success(Strings.locations.commandSuccessfully);
      setOpenRightSideBar(false);
      reloadTable();
    }
  }, [isSuccessTurnOffLocation, isSuccessTurnOnLocation]);

  useEffect(() => {
    if (isSuccessDeleteOrganization) {
      toast.success(Strings.organizations.deleteOrganization);
      setOpenRightSideBar(false);
      reloadTable();
    }
  }, [isSuccessDeleteOrganization]);

  const deleteLocationOrganization = () => {
    const locationIds = selectedLocationsData.map((loc) => ({
      locationId: loc.locationId,
      userId: loc.ownerId
    }));
    const organizationIds = selectedOrganizations?.map(
      (org) => org.organizationId
    );
    if (locationIds && locationIds.length) {
      deleteLocationByRole(locationIds);
    }
    if (organizationIds && organizationIds.length) {
      deleteOrganization(organizationIds);
    }
  };

  const handleResetLocation = () => {
    const locationIds = selectedLocations;
    if (locationIds && locationIds.length) {
      resetLocation(locationIds.map((id) => id.toString()));
    }
  };

  return (
    <StyledContainerItems>
      <SideBarSimpleMethodItem
        name={Strings.locations.transferOwnership}
        action={() => {
          openRightSideBarForMethod(viewRightSideBarEnum.transferLocation);
        }}
        successAction={false}
        textSuccess="success"
      />
      {selectedLocations && !selectedOrganizations && (
        <SideBarSimpleMethodItem
          name={Strings.locations.shareLocationsWithTargets}
          action={() => {
            openRightSideBarForMethod(
              viewRightSideBarEnum.shareLocationsWithTargets
            );
          }}
          successAction={false}
          textSuccess="success"
        />
      )}
      <SideBarSimpleMethodItem
        name={Strings.locations.moveToOrg}
        action={() => {
          openRightSideBarForMethod(viewRightSideBarEnum.moveLocation);
        }}
        successAction={false}
        textSuccess="success"
      />
      <SideBarSimpleMethodItem
        name={Strings.serverAccess.devices.turnOffDevices}
        action={() => {
          turnOffLocation(selectedLocations || []);
        }}
        successAction={false}
        textSuccess="success"
      />
      <SideBarSimpleMethodItem
        name={Strings.serverAccess.devices.turnOn}
        action={() => {
          turnOnLocation(selectedLocations || []);
        }}
        successAction={false}
        textSuccess="success"
      />
      <SideBarResetActionWithPopup
        onReset={handleResetLocation}
        titleConfirmation={Strings.locations.resetLocation}
        textConfirmation={Strings.common.thisActionIsIrreversible}
        textIcon={Strings.locations.resetLocation}
      />
      <SideBarDeleteActionWithPopup
        onDelete={() => deleteLocationOrganization()}
        titleConfirmation={Strings.locations.deleteLocationTitle}
        textConfirmation={Strings.common.thisActionIsIrreversible}
        textIcon={Strings.common.delete}
      />
    </StyledContainerItems>
  );
};
